import { v4 as nanoid } from 'uuid'

import { VersionedEvent } from '../'
import { PlayerEntity } from '../../data/PlayerEntity'
import { Player } from '../../model/Player'

enum PlayerEventKind {
  Created = 'poker:player:created',
  BeganPlay = 'poker:player:began_play',
  Fold = 'poker:player:folded',
  AllIn = 'poker:player:allin',
  Out = 'poker:player:out',
  SitOut = 'poker:player:sitout',
  ForeverOut = 'poker:player:foreverout',
  Waiting = 'poker:player:waiting',
  Disconnected = 'poker:player:disconnected',
  PaidBet = 'poker:player:paid_bet',
  ReceivedCards = 'poker:player:received_cards',
  ReturnedCards = 'poker:player:returned_cards',
  BuyIn = 'poker:player:buyin',
  DoNoAction = 'poker:player:donoaction',
  Observer = 'poker:player:observer',
  HideCards = 'poker:player:hide_cards',
  ShowCardsForTeacher = 'poker:player:show_cards_for_teacher',
  ShowCardsForEveryone = 'poker:player:show_cards_for_everyone'
}

interface PlayerEvent extends VersionedEvent {
  id: string
  timestamp: number
  kind: PlayerEventKind
  player: PlayerEntity
}

class PlayerEvents {
  static created(player: Player) {
    return PlayerEvents.createEvent(PlayerEventKind.Created, player)
  }

  static beganPlay(player: Player) {
    return PlayerEvents.createEvent(PlayerEventKind.BeganPlay, player)
  }

  static fold(player: Player) {
    return PlayerEvents.createEvent(PlayerEventKind.Fold, player)
  }

  static allin(player: Player) {
    return PlayerEvents.createEvent(PlayerEventKind.AllIn, player)
  }

  static out(player: Player) {
    return PlayerEvents.createEvent(PlayerEventKind.Out, player)
  }

  static sitout(player: Player) {
    return PlayerEvents.createEvent(PlayerEventKind.SitOut, player)
  }

  static foreverOut(player: Player) {
    return PlayerEvents.createEvent(PlayerEventKind.ForeverOut, player)
  }

  static waiting(player: Player) {
    return PlayerEvents.createEvent(PlayerEventKind.Waiting, player)
  }

  static observer(player: Player) {
    return PlayerEvents.createEvent(PlayerEventKind.Observer, player)
  }

  static disconnected(player: Player) {
    return PlayerEvents.createEvent(PlayerEventKind.Disconnected, player)
  }

  static paidBet(player: Player) {
    return PlayerEvents.createEvent(PlayerEventKind.PaidBet, player)
  }

  static receivedCards(player: Player) {
    return PlayerEvents.createEvent(PlayerEventKind.ReceivedCards, player)
  }

  static returnedCards(player: Player) {
    return PlayerEvents.createEvent(PlayerEventKind.ReturnedCards, player)
  }

  static buyChips(player: Player) {
    return PlayerEvents.createEvent(PlayerEventKind.BuyIn, player)
  }

  static doNoAction(player: Player) {
    return PlayerEvents.createEvent(PlayerEventKind.DoNoAction, player)
  }

  static hideCards(player: Player) {
    return PlayerEvents.createEvent(PlayerEventKind.HideCards, player)
  }

  static showCardsForTeacher(player: Player) {
    return PlayerEvents.createEvent(PlayerEventKind.ShowCardsForTeacher, player)
  }

  static showCardsForEveryone(player: Player) {
    return PlayerEvents.createEvent(PlayerEventKind.ShowCardsForEveryone, player)
  }

  static createEvent(eventKind: PlayerEventKind, player: Player) {
    return <PlayerEvent>{
      id: nanoid(),
      name: eventKind,
      timestamp: Date.now(),
      kind: eventKind,
      player: PlayerEntity.createFromPlayer(player)
    }
  }
}

export { PlayerEventKind, type PlayerEvent, PlayerEvents }
